import { z } from 'zod';

/**
 * Wildfire report schema — mirrors the legacy Yup schema in WildfireForm.jsx.
 * Numeric inputs use `z.coerce.number()` because HTML <input type="number">
 * sends strings.
 */
export const FIRE_TYPES = [
  'veld',
  'structural',
  'rubbish',
  'permit_burn',
] as const;
export type FireType = (typeof FIRE_TYPES)[number];

export const FIRE_TYPE_LABELS: Record<FireType, string> = {
  veld: 'Veld',
  structural: 'Structural',
  rubbish: 'Rubbish',
  permit_burn: 'Permit burn',
};

/**
 * One assisting party on a fire. `providerKey` ("FPA#3" / "ORG#7") is the
 * identity that matters downstream — it is what the stats aggregate on, so a
 * provider that gets renamed stays one row. kind/id are carried alongside it
 * only because the API takes them separately.
 */
export const MutualAidRowSchema = z.object({
  providerKey: z.string().min(1, 'Choose who assisted'),
  providerKind: z.enum(['fpa', 'org']),
  providerId: z.number(),
  providerName: z.string().optional().default(''),
  stationIds: z.array(z.number()).default([]),
  people: z.coerce.number().int('Must be a whole number').min(0, 'Cannot be negative').default(0),
  vehicles: z.coerce.number().int('Must be a whole number').min(0, 'Cannot be negative').default(0),
  vehicleDetails: z.string().max(255).optional().default(''),
  hours: z.coerce.number().min(0, 'Cannot be negative').default(0),
  note: z.string().max(500).optional().default(''),
});

export type MutualAidRow = z.infer<typeof MutualAidRowSchema>;

export const WildfireSchema = z.object({
  // Classification — only "veld" contributes to GUFPA stats.
  fireType: z.enum(FIRE_TYPES).default('veld'),

  // Time
  startTime: z.date({ required_error: 'Start time is required' }),
  endTime: z.date().nullable().optional(),

  // Location-related
  areaSize: z
    .union([z.coerce.number().positive('Area size must be positive'), z.literal('')])
    .optional()
    .nullable(),
  suburb: z.string().max(200).optional().default(''),
  address: z.string().max(255).optional().default(''),

  // People — auto-derived from firefighters list when present, else manual.
  peopleInvolved: z.coerce
    .number()
    .int('Must be a whole number')
    .min(0, 'Cannot be negative')
    .default(0),
  firefighterIds: z.array(z.number()).default([]),
  guestFirefighters: z.array(z.string().min(1).max(120)).default([]),
  manHours: z.coerce.number().min(0, 'Cannot be negative').default(0),
  injuries: z.coerce
    .number()
    .int('Must be a whole number')
    .min(0, 'Cannot be negative')
    .default(0),

  // Damage
  possibleCause: z.string().max(2000).optional().default(''),
  housesDamagedCount: z.coerce
    .number()
    .int('Must be a whole number')
    .min(0, 'Cannot be negative')
    .default(0),
  housesEstimatedDamage: z.coerce
    .number()
    .min(0, 'Cannot be negative')
    .default(0),
  livestockInjured: z.coerce
    .number()
    .int('Must be a whole number')
    .min(0, 'Cannot be negative')
    .default(0),
  livestockEstimatedDamage: z.coerce
    .number()
    .min(0, 'Cannot be negative')
    .default(0),

  // Refs
  fireDeptRef: z.string().max(200).optional().default(''),
  policeRef: z.string().max(200).optional().default(''),

  // Illegal/unauthorised flag — orthogonal to fireType; any fire can be illegal.
  isIllegal: z.boolean().default(false),

  // Member linkage — optional on any fire. Ties the incident to an FPA member
  // (e.g. the party responsible for an illegal burn) and/or parcel.
  memberId: z.number().nullable().optional(),
  memberName: z.string().optional().default(''),
  parcelId: z.number().nullable().optional(),

  // Mutual aid — assistance RECEIVED from other FPAs and outside agencies.
  // Deliberately separate from peopleInvolved: that field feeds man-hours and
  // the GUFPA export, and folding outside agencies into it would move numbers
  // that have already been reported.
  mutualAid: z.array(MutualAidRowSchema).default([]),

  // Notes & visibility
  internalNotes: z.string().max(5000).optional().default(''),
  externalNotes: z.string().max(5000).optional().default(''),
  isActive: z.boolean().default(false),
  isPublic: z.boolean().default(true),
}).superRefine((val, ctx) => {
  // Cross-field: catch "end before start" client-side so the user sees an
  // inline field error instead of a server 422 on submit.
  if (val.startTime && val.endTime && val.endTime <= val.startTime) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['endTime'],
      message: 'End time must be after the start time',
    });
  }

  // One row per organisation now that stations are multi-select, so a repeat
  // of the same provider is always a double entry — it would silently
  // inflate that provider people count. Several depots go on the one row.
  const seen = new Set<string>();
  (val.mutualAid || []).forEach((row, index) => {
    if (seen.has(row.providerKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mutualAid', index, 'providerKey'],
        message: 'Already listed — add their other stations to that row instead',
      });
    }
    seen.add(row.providerKey);
  });
});

export type WildfireValues = z.infer<typeof WildfireSchema>;

/**
 * Field groupings per wizard step — used by `form.trigger([...])`.
 * Keys MUST stay in step order and match each STEPS[i].id; the wizard looks
 * fields up by step id (STEP_FIELDS[STEPS[step].id]).
 */
export const STEP_FIELDS = {
  location: ['areaSize', 'suburb', 'address'] as const,
  details: ['startTime', 'endTime', 'fireType', 'isIllegal', 'isActive', 'memberId'] as const,
  damage: [
    'peopleInvolved',
    'manHours',
    'injuries',
    'possibleCause',
    'housesDamagedCount',
    'housesEstimatedDamage',
    'livestockInjured',
    'livestockEstimatedDamage',
    'fireDeptRef',
    'policeRef',
  ] as const,
  aid: ['mutualAid'] as const,
  notes: ['internalNotes', 'externalNotes', 'isPublic'] as const,
  // Final review is read-only — nothing new to validate here.
  review: [] as const,
};
